import { t } from 'i18n';
import { TFunction } from 'i18next';
import _ from 'lodash';
import $$api from 'models/api';
import $$auth from 'models/auth';
import { $global, storage } from 'models/store-global';

import AlertsService from 'services/AlertsService';
import DateService from 'services/DateService';
import { isna } from 'utils/common-helper';

import { MyStore } from 'types/Store';
import { type AdvisorParticipant } from 'types/zod/advisor-participant';
import { type Participant } from 'types/zod/participant';
import { type ParticipantReference, type ParticipantReferences } from 'types/zod/participant-references';
import { type ParticipantRole } from 'types/zod/participant-role';
import { type Question } from 'types/zod/question';
import { type Summary } from 'types/zod/summary';
import { type SummaryParticipant } from 'types/zod/summary-participant';

const ParticipantsService = {
    getFullName: (participant: Participant) => {
        if (participant.participant_type_code && participant.participant_type_code !== 'Individual') {
            return participant.organization_name;
        }

        const nameParts = [];

        if (participant.first_name) nameParts.push(participant.first_name);
        if (participant.middle_name) nameParts.push(participant.middle_name);
        if (participant.last_name) nameParts.push(participant.last_name);

        let result = nameParts.join(' ');

        if (participant.suffix) result += participant.suffix;

        return result;
    },

    getSuffixes: (): any[] => {
        return t('participantSuffix', '', { returnObjects: true }) || [];
    },

    getApproximateAges: (t: TFunction): any[] => {
        const ages: any[] = t('approx-age:age_ranges', '', { returnObjects: true }) || [];
        storage.set(
            (store): MyStore => ({
                ...store,
                temp: {
                    ...(store.temp || {}),
                    ages: ages,
                },
            }),
        );
        return ages;
    },

    getRelationships: (): any[] => {
        return t('observerRelationship', '', { returnObjects: true }) || [];
    },

    isAdvisor: (participant: Participant): boolean => {
        return !!ParticipantsService.getRelationships().find((r) => r.value === participant.observer_relationship)
            ?.isAdvisor;
    },

    isDobRequired: (question: Question) => {
        return (question._widget || {}).required_dob || false;
    },

    isAgeRequired: (question: Question) => {
        return (question._widget || {}).required_age || false;
    },

    getAge: (participant: Participant) => {
        if (participant.date_of_birth) {
            return DateService.getAge(participant.date_of_birth);
        }
        return Math.floor((participant.approx_age || 0) / 365);
    },

    deleteObserver: async (id: number) => {
        await $$api.participants.deleteObserverFx(id);
        await $$api.participants.getCommunityFx(undefined);
    },

    setNewUserEmail: async (email: string) => {
        await $$api.participants.setNewUserEmailFx(email);
        await $$api.participants.getMeFx();
        AlertsService.showSuccess(t(`changeEmail.success_message`));
    },

    isAddressVisible: (participant: Participant) => {
        return (
            !isna(participant?.address?.domicile?.city) ||
            !isna(participant?.address?.domicile?.state_name) ||
            !isna(participant?.address?.domicile?.zip)
        );
    },

    getParticipant: async (id: number, useCache?: boolean) => {
        if (useCache) {
            const participant = $global.map((state) => state.participants?.[id]).getState();
            if (participant) {
                return participant;
            }
        }
        return await $$api.participants.getParticipantFx(id);
    },

    isNotifiableParticipant: (participant: Participant) => {
        return ParticipantsService.getAge(participant) >= 13 || !participant.participant_id;
    },

    isNotifiableRole: (participant: Participant, role: ParticipantRole) => {
        return role.notifiable && ParticipantsService.isNotifiableParticipant(participant);
    },

    $advisors: $global.map((state) =>
        (Object.values(state.participants || {}) as AdvisorParticipant[]).filter(
            (p) => Array.isArray(p.systemRoles) && p.systemRoles.includes('advisor'),
        ),
    ),

    formatPhone: (phone: string) => {
        if (!phone || phone.length !== 12 || !phone.startsWith('+1')) {
            return phone;
        }
        return '+1 (' + phone.substring(2, 5) + ') ' + phone.substring(5, 8) + '-' + phone.substring(8);
    },

    getParticipantsFromSummary: (summary: Summary, references: ParticipantReferences): SummaryParticipant[] => {
        const processRoles = (p: SummaryParticipant, roles: string[]) => {
            for (const r of roles) {
                let roleToAdd = r;
                if (ParticipantsService.isTrusteeRole(r)) {
                    const refs = ((references[p.participant_id] || []) as ParticipantReference[]).filter(
                        (r) => r.qInstanceId === summary.q_instance_id,
                    );
                    for (const ref of refs) {
                        if (ref.role === r) {
                            const context = ParticipantsService.getContextFromReference(ref);
                            roleToAdd = context ? `${r}_${context}` : r;
                            const hasRole = p.roles.includes(roleToAdd);
                            if (!hasRole) {
                                p.roles.push(roleToAdd);
                            }
                        }
                    }
                }
                const hasRole = p.roles.includes(roleToAdd);
                if (!hasRole) {
                    p.roles.push(roleToAdd);
                }
            }
        };

        let participants = [];
        for (const p of summary.participants) {
            participants.push(p);
            const roles = p.roles.slice();
            p.roles = [];
            processRoles(p, roles);
        }

        for (const i of summary.includes || []) {
            for (const p of i.participants || []) {
                const alreadyFoundParticipant = participants.find(
                    (participant: any) => participant.participant_id === p.participant_id,
                );
                if (alreadyFoundParticipant) {
                    processRoles(alreadyFoundParticipant, p.roles);
                } else {
                    participants.push(p);
                    const roles = p.roles.slice();
                    p.roles = [];
                    processRoles(p, roles);
                }
            }
        }

        for (const p of participants) {
            if (p.email && (p.phone_number || ParticipantsService.isPhoneRequired(p))) {
                p.contactOption = 'emailAndPhone';
            } else if (p.email) {
                p.contactOption = 'email';
            } else if (p.phone_number) {
                p.contactOption = 'phone';
            }
        }

        const originator = $$auth.$currentParticipant.getState() as SummaryParticipant;
        originator.roles = ['Originator'];
        if (originator.domicile_state_code && !originator?.address?.domicile) {
            // @ts-ignore
            originator.address = { domicile: { state_code: originator.domicile_state_code } };
        }

        participants = participants.filter((p) => p.participant_id !== originator.participant_id);
        participants.unshift(originator);

        return participants;
    },

    shouldFillInfo: (participants: SummaryParticipant[]) => {
        const originator = $$auth.$currentParticipant.getState() as SummaryParticipant;
        for (const p of participants) {
            if (ParticipantsService.isDobRequiredByRole(p)) {
                if (!p.date_of_birth) {
                    return true;
                }
            } else {
                if (p.approx_age == null && !p.date_of_birth && p.participant_type_code === 'Individual') {
                    return true;
                }
            }

            if (
                ParticipantsService.isContactInfoRequired(p) &&
                !p.email &&
                !p.phone_number &&
                (p.approx_age || 0) > 0
            ) {
                return true;
            }

            if (p.participant_id === originator.participant_id && !p.phone_number) {
                return true;
            }

            if (ParticipantsService.isAddressRequired(p)) {
                if (!p?.address?.domicile || _.isEmpty(p?.address?.domicile)) {
                    return true;
                }
            }

            if (
                ParticipantsService.isContactInfoRequired(p) &&
                ParticipantsService.isPhoneRequired(p) &&
                !p.phone_number &&
                (p.approx_age || 0) > 0
            ) {
                return true;
            }
        }

        return false;
    },

    isDobRequiredByRole: (p: SummaryParticipant) => {
        return p.roles.includes('Spouse_or_Partner') || p.roles.includes('Originator');
    },

    isContactInfoRequired: (p: SummaryParticipant) => {
        return p.participant_type_code === 'Individual' && (p.roles.length > 1 || p.roles[0] !== 'Spouse_or_Partner');
    },

    isAddressRequired: (p: SummaryParticipant) => {
        return (
            p.participant_type_code === 'Charity' ||
            p.roles.some((r) =>
                [
                    'Originator',
                    'Spouse_or_Partner',
                    'Designated_Person',
                    'Represented_Person',
                    'Agent',
                    'Alternate_Agent',
                    'Co_Agent',
                    'Alternate_Co_Agent',
                    'HEALTH_CARE_AGENT',
                    'ALTERNATE_HEALTH_CARE_AGENT',
                    'HEALTH_CARE_CO-AGENT',
                    'SUBSTITUTE_HEALTH_CO-AGENT',
                ].includes(r),
            ) ||
            !!p.address?.domicile
        );
    },

    isPhoneRequired: (p: SummaryParticipant) => {
        return p.roles.some((r) =>
            [
                'Agent',
                'Alternate_Agent',
                'Alternate_Co_Agent',
                'Co_Agent',
                'Substitute_Co_Agent',
                'HEALTH_CARE_AGENT',
                'ALTERNATE_HEALTH_CARE_AGENT',
                'HEALTH_CARE_CO-AGENT',
                'SUBSTITUTE_HEALTH_CO-AGENT',
            ].includes(r),
        );
    },

    createComparator: (answerParticipants: Participant[]) => {
        return (p1: Participant, p2: Participant) => {
            if (answerParticipants) {
                const apIds = answerParticipants.map((p: Participant) => p.participant_id);
                if (apIds.includes(p1.participant_id) && !apIds.includes(p2.participant_id)) {
                    return -1;
                }
                if (apIds.includes(p2.participant_id) && !apIds.includes(p1.participant_id)) {
                    return 1;
                }
            }
            return (ParticipantsService.getFullName(p1) || '').localeCompare(ParticipantsService.getFullName(p2) || '');
        };
    },

    isTrusteeRole: (r: string) => {
        return ['Trustee', 'Successor_Trustee', 'Successor_Co_Trustee', 'Substitute_Co_Trustee', 'Co_Trustee'].includes(
            r,
        );
    },

    getContextFromReference: (ref: ParticipantReference) => {
        if (ref.path.startsWith('estate_distros.')) {
            return 'estate';
        } else if (ref.path.startsWith('specific_distros.')) {
            return 'specific';
        }
        return '';
    },

    getRoleWithContext: (participantId: number, role: string, references: ParticipantReferences) => {
        if (!ParticipantsService.isTrusteeRole(role)) {
            return role;
        }
        const ref = (references[participantId] || []).find((r) => r.role === role);
        if (ref) {
            const context = ParticipantsService.getContextFromReference(ref);
            if (context) {
                return `${role}_${context}`;
            }
        }
        return role;
    },
};

export default ParticipantsService;
